import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { MetaTags } from '../../components/Metatags/Metatags';
import { useDispatchPromise } from '../../utils/AppUtils';
import { usePagination } from '../../hook/usePagination';
import { getManageEvent, clearManageEventError, updateEvent } from '../../redux/events/action';
import { EventManageContainer } from '../../components/ScreenContainer/EventContainer/EventManageContainer';
import { useDispatch } from 'react-redux';
import { ICreateEvent, IManagedEventSales, IManagedEventTicket } from '../../redux/events/types';
import { managedEventSelector } from './selectors';
import { FeeStructure, IRawFeeStructure } from '../../types/global/helper';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

type UpdateField = { key: keyof ICreateEvent; value: any } | { key: keyof ICreateEvent; value: any }[];

const ManageEvent = () => {
    const params = useParams();
    const dispatchPromise = useDispatchPromise();
    const dispatch = useDispatch();
    const { id } = params;
    const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
    const [startDate, setStartDate] = useState<string | undefined>(undefined);
    const [endDate, setEndDate] = useState<string | undefined>(undefined);
    const [ticketType, setTicketType] = useState<string | undefined>(undefined);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [activeTab, setActiveTab] = useState<string>('details');

    // Pagination for sales data
    const {
        pagination: salePagination,
        setPagination: setSalePagination,
        handleRowsChange: handleSaleRowsChange,
        handlePageChange: handleSalePageChange,
    } = usePagination(1, 20);

    // Pagination for tickets data
    const {
        pagination: ticketPagination,
        setPagination: setTicketPagination,
        handleRowsChange: handleTicketRowsChange,
        handlePageChange: handleTicketPageChange,
    } = usePagination(1, 20);

    const forceNoRefresh = useRef<boolean>(false);

    // Get data from redux store
    const {
        managedEvent,
        managedEventLoading = false,
        managedEventError,
        updatingEvent,
    } = useSelector(managedEventSelector);

    const getData = useCallback(async () => {
        const response: any = await dispatchPromise(
            getManageEvent({
                eventId: id!,
                salePage: salePagination.page,
                saleLimit: salePagination.rows,
                ticketPage: ticketPagination.page,
                ticketLimit: ticketPagination.rows,
                searchTerm,
                startDate,
                endDate,
                ticketType,
                sortOrder,
            }),
        );

        // Update pagination state based on response
        if (response?.data) {
            if (response.data.sales?.pagination) {
                const { currentPage, pageSize, totalCount } = response.data.sales.pagination;
                setSalePagination({
                    page: currentPage,
                    rows: pageSize,
                    totalCount,
                });
            }

            if (response.data.tickets?.pagination) {
                const { currentPage, pageSize, totalCount } = response.data.tickets.pagination;
                setTicketPagination({
                    page: currentPage,
                    rows: pageSize,
                    totalCount,
                });
            }
        }

        forceNoRefresh.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        id,
        sortOrder,
        ticketType,
        endDate,
        salePagination.page,
        searchTerm,
        ticketPagination.page,
        salePagination.rows,
        startDate,
        ticketPagination.rows,
    ]);

    const handleUpdateField = useCallback(
        (input: UpdateField) => {
            if (!id) return;

            let body: Partial<ICreateEvent> = {};
            if (Array.isArray(input)) {
                input.forEach(({ key, value }) => {
                    body[key] = value;
                });
            } else {
                body[input.key] = input.value;
            }

            dispatch(
                updateEvent({
                    id,
                    body,
                }),
            );
        },
        [id, dispatch],
    );

    const handleCancelEvent = useCallback(() => {
        if (!id) return;

        dispatch(
            updateEvent({
                id,
                body: {
                    state: 'cancelled' as const,
                },
            }),
        );
    }, [id, dispatch]);

    // Handle clearing errors and retrying
    const handleClearError = useCallback(() => {
        dispatch(clearManageEventError());
        getData();
    }, [dispatch, getData]);

    useEffect(() => {
        getData();
    }, [getData]);

    // Handle search input change
    const handleSearchChange = (searchText: string) => {
        forceNoRefresh.current = true;
        setSearchTerm(searchText);
    };

    // Handle date filter changes
    const handleStartDateChange = (date: string) => {
        setStartDate(date);
    };

    const handleEndDateChange = (date: string) => {
        setEndDate(date);
    };

    // Handle ticket type filter change
    const handleTicketTypeChange = (type: string) => {
        setTicketType(type);
    };

    // Handle sort order change
    const handleSortOrderChange = (order: 'asc' | 'desc') => {
        setSortOrder(order);
    };

    // Clear filter handlers
    const handleClearSearch = () => {
        forceNoRefresh.current = true;
        setSearchTerm(undefined);
        getData();
    };

    const handleClearStartDate = () => {
        forceNoRefresh.current = true;
        setStartDate(undefined);
        getData();
    };

    const handleClearEndDate = () => {
        forceNoRefresh.current = true;
        setEndDate(undefined);
        getData();
    };

    const handleClearTicketType = () => {
        forceNoRefresh.current = true;
        setTicketType(undefined);
        getData();
    };

    const handleClearAllFilters = () => {
        forceNoRefresh.current = true;
        setSearchTerm(undefined);
        setStartDate(undefined);
        setEndDate(undefined);
        setTicketType(undefined);
        getData();
    };

    // Handle tab change
    const handleTabChange = (tab: string) => {
        setActiveTab(tab);
    };

    // Handle edit event details
    const handleEditDetails = (sectionName: string) => {
        console.log(`Edit ${sectionName}`);
    };

    // Handle edit discount
    const handleEditDiscount = (discountId: string) => {
        console.log(`Edit discount ${discountId}`);
    };

    // Handle create new discount
    const handleCreateDiscount = () => {
        console.log('Create new discount');
    };

    // Handle edit fee structure
    const handleEditFee = (fee: IRawFeeStructure) => {
        // Implementation would call the API to update the fee
        const currentFeeIndex = managedEvent?.event?.fee_structure?.findIndex(f => fee._id === f._id);
        if (currentFeeIndex !== undefined && currentFeeIndex > -1) {
            const payload = [...(managedEvent?.event?.fee_structure || [])];
            payload[currentFeeIndex] = fee as unknown as FeeStructure;
            handleUpdateField([{ key: 'fee_structure', value: payload }]);
        }
    };

    const handleDeleteFee = (feeId: string) => {
        const currentFeeIndex = managedEvent?.event?.fee_structure?.filter(f => feeId !== f._id);
        handleUpdateField([{ key: 'fee_structure', value: currentFeeIndex }]);
    };

    // Handle add new fee
    const handleAddFee = (fee: any) => {
        const currentFees = managedEvent?.event?.fee_structure || [];
        const paylaod = [...currentFees, fee];
        // Implementation would call the API to add a new fee
        handleUpdateField([{ key: 'fee_structure', value: paylaod }]);
    };

    const handleDownload = (dataMap: any) => {
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(dataMap);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
        const excelBuffer = XLSX.write(workbook, {
            bookType: 'xlsx',
            type: 'array',
        });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(blob, `${managedEvent?.event?.name}sales-data.xlsx`);
    };

    // Handle export CSV
    const handleExportCSV = (type: 'sales' | 'tickets', data: IManagedEventSales | IManagedEventTicket[]) => {
        switch (type) {
            case 'sales':
                const clonedSalesData: IManagedEventSales = data as IManagedEventSales;
                const salesMap = clonedSalesData.data.map(d => ({
                    email: d.email_used,
                    sale_price: d.formatted_usd_discounted_price,
                    is_discounted: d.is_discounted ? 'true' : 'false',
                    customer: d.name_used,
                    transaction_currency: d.transaction_currency,
                    exchange_rate: d.exchange_rate,
                    category: d.category,
                    event: d.linked_event,
                    event_type: d.linked_event_type,
                }));
                handleDownload(salesMap);
                break;
            case 'tickets':
                const clonedTicketsData: IManagedEventTicket[] = data as IManagedEventTicket[];
                const ticketsMap = clonedTicketsData.map(ticket => ({
                    email: ticket.recipient_email,
                    seat: ticket.seat_info,
                    ticket_id: ticket.ticket_id,
                    type: ticket.ticket_type,
                    checked_in: ticket.checked_in ? 'true' : 'false',
                }));
                handleDownload(ticketsMap);
                break;
        }
    };

    return (
        <MetaTags
            title={`Manage Event | ${managedEvent?.event?.name || 'Loading...'}`}
            description={managedEvent?.event?.description || ''}>
            <EventManageContainer
                event={managedEvent?.event}
                sales={managedEvent?.sales}
                tickets={managedEvent?.tickets}
                discounts={managedEvent?.event_discounts}
                totalAttendees={managedEvent?.total_attendees}
                loading={managedEventLoading}
                updatingInProgress={updatingEvent}
                error={managedEventError}
                activeTab={activeTab}
                salePagination={salePagination}
                ticketPagination={ticketPagination}
                sortOrder={sortOrder}
                onSearchChange={handleSearchChange}
                onStartDateChange={handleStartDateChange}
                onEndDateChange={handleEndDateChange}
                onTicketTypeChange={handleTicketTypeChange}
                onSortOrderChange={handleSortOrderChange}
                onTabChange={handleTabChange}
                onSaleRowsChange={handleSaleRowsChange}
                onSalePageChange={handleSalePageChange}
                onTicketRowsChange={handleTicketRowsChange}
                onTicketPageChange={handleTicketPageChange}
                onEditDetails={handleEditDetails}
                onEditDiscount={handleEditDiscount}
                onCreateDiscount={handleCreateDiscount}
                onEditFee={handleEditFee}
                onDeleteFee={handleDeleteFee}
                onAddFee={handleAddFee}
                onExportCSV={handleExportCSV}
                onClearSearch={handleClearSearch}
                onClearStartDate={handleClearStartDate}
                onClearEndDate={handleClearEndDate}
                onClearTicketType={handleClearTicketType}
                onClearAllFilters={handleClearAllFilters}
                onClearError={handleClearError}
                onCancelEvent={handleCancelEvent}
                onImageChange={(imageUrl, rawFile) => {
                    handleUpdateField([
                        { key: 'feature_image', value: [rawFile] },
                        { key: 'temp_feature_image', value: imageUrl },
                    ]);
                }}
            />
        </MetaTags>
    );
};

export { ManageEvent };
